<template>
  <div class="business-settings">
    <b-card no-body class="border-0 shadow-sm overflow-hidden">
      <b-card-header class="bg-light-primary border-bottom py-1">
        <b-card-title class="font-medium-3 d-flex align-items-center">
          <feather-icon icon="SettingsIcon" size="20" class="mr-50 text-primary" />
          <span class="text-primary font-weight-bolder">{{ title }}</span>
        </b-card-title>
      </b-card-header>

      <b-card-body class="pt-2">
        <div v-if="pageLoading" class="text-center p-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1">Loading Content...</p>
        </div>

        <template v-else>
          <template v-if="isThemeType() && activeThemeTab === 'single_product'">
                <b-col cols="12" class="mb-2">
                  <div class="theme-subtabs-wrap">
                    <button
                      v-for="tab in singleProductSubTabs"
                      :key="tab.key"
                      type="button"
                      class="theme-subtab-btn"
                      :class="{ active: singleProductActiveSubTab === tab.key }"
                      @click="setSingleProductSubTab(tab.key)"
                    >
                      <feather-icon :icon="tab.icon" size="14" class="mr-50" />
                      <span>{{ tab.label }}</span>
                    </button>
                  </div>
                </b-col>

                <b-col v-if="singleProductActiveSubTab === 'sorting'" cols="12" class="mb-2">
                  <div class="theme-button-card">
                    <div class="theme-button-card__header">
                      <div>
                        <h6 class="mb-25">Button Order</h6>
                        <small class="text-muted">Drag and drop the buttons below. Frontend order will follow this exact order after save.</small>
                      </div>
                    </div>

                    <div class="single-product-sort-list">
                      <div
                        v-for="card in orderedSingleProductButtonCards"
                        :key="card.key"
                        class="single-product-sort-item"
                        :class="{ dragging: draggedSingleProductButtonKey === card.key }"
                        draggable="true"
                        @dragstart="handleSingleProductDragStart(card.key)"
                        @dragover.prevent
                        @drop="handleSingleProductDrop(card.key)"
                        @dragend="handleSingleProductDragEnd"
                      >
                        <div class="single-product-sort-item__handle">
                          <feather-icon icon="MenuIcon" size="16" />
                        </div>
                        <div class="single-product-sort-item__content">
                          <strong>{{ card.title }}</strong>
                          <small class="text-muted d-block">{{ card.description }}</small>
                        </div>
                        <span class="single-product-sort-item__badge">Drag</span>
                      </div>
                    </div>
                  </div>
                </b-col>

                <b-col
                  v-for="card in orderedSingleProductButtonCards"
                  v-show="singleProductActiveSubTab === card.key"
                  :key="card.key"
                  cols="12"
                  class="mb-2"
                >
                  <div class="theme-button-card">
                    <div class="theme-button-card__header">
                      <div>
                        <h6 class="mb-25">{{ card.title }}</h6>
                        <small class="text-muted">{{ card.description }}</small>
                      </div>
                    </div>

                    <b-row>
                      <b-col
                        v-for="key in card.fields.filter(fieldKey => !fieldKey.endsWith('_sort_order'))"
                        :key="key"
                        cols="12"
                        md="6"
                        class="mb-1"
                      >
                        <div class="theme-field-card theme-field-card--nested">
                          <label class="font-weight-bold text-dark small text-uppercase mb-50 d-block">
                            {{ formatLabel(key) }}
                          </label>

                          <div
                            v-if="isThemeColorField(key)"
                            class="theme-color-field bg-white border rounded p-1"
                          >
                            <div class="d-flex align-items-center">
                              <input
                                v-model="settings[key]"
                                type="color"
                                class="theme-color-input mr-1"
                              >
                              <b-form-input
                                v-model="settings[key]"
                                class="form-control-lg"
                                :placeholder="getColorPlaceholder(key)"
                                @blur="normalizeColorValue(key)"
                              />
                            </div>
                            <small class="text-muted d-block mt-50">
                              Use hex color like #000000
                            </small>
                          </div>

                          <div
                            v-else-if="isThemeBooleanField(key)"
                            class="bg-white border rounded px-1 py-75"
                          >
                            <b-form-checkbox
                              v-model="settings[key]"
                              switch
                              value="1"
                              unchecked-value="0"
                            >
                              {{ settings[key] === '1' || settings[key] === 1 || settings[key] === true ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                          </div>

                          <div
                            v-else-if="isThemeFontFamilyField(key)"
                            class="bg-white border rounded p-1"
                          >
                            <b-form-select
                              v-model="settings[key]"
                              :options="fontFamilyOptions"
                              class="form-control-lg"
                            />
                            <small class="text-muted d-block mt-50">
                              Preset font family for button typography
                            </small>
                          </div>

                          <div
                            v-else-if="isThemeSelectField(key)"
                            class="bg-white border rounded p-1"
                          >
                            <b-form-select
                              v-model="settings[key]"
                              :options="getThemeSelectOptions(key)"
                              class="form-control-lg"
                            />
                            <small class="text-muted d-block mt-50">
                              Select a CSS preset value
                            </small>
                          </div>

                          <div
                            v-else-if="isThemeTypographyField(key)"
                            class="bg-white border rounded p-1"
                          >
                            <b-input-group class="input-group-merge">
                              <b-input-group-prepend is-text>
                                <feather-icon :icon="getTypographyIcon(key)" size="16" />
                              </b-input-group-prepend>
                              <b-form-input
                                v-model="settings[key]"
                                class="form-control-lg"
                                :placeholder="getTypographyPlaceholder(key)"
                              />
                            </b-input-group>
                            <small class="text-muted d-block mt-50">
                              {{ getTypographyHelpText(key) }}
                            </small>
                          </div>

                          <b-input-group v-else class="input-group-merge">
                            <b-input-group-prepend is-text>
                              <feather-icon :icon="getIcon(key)" size="16" />
                            </b-input-group-prepend>
                            <b-form-input
                              v-model="settings[key]"
                              class="form-control-lg"
                            />
                          </b-input-group>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </template>

              <template v-else-if="isThemeType()">
            <div class="theme-tabs-wrap mb-2">
              <div class="theme-tabs-scroll">
                <button
                  v-for="tab in themeTabs"
                  :key="tab.key"
                  type="button"
                  class="theme-tab-btn"
                  :class="{ active: activeThemeTab === tab.key }"
                  @click="activeThemeTab = tab.key"
                >
                  <feather-icon :icon="tab.icon" size="15" class="mr-50" />
                  <span>{{ tab.label }}</span>
                </button>
              </div>
            </div>

            <div class="theme-section-hero mb-2">
              <div>
                <h5 class="mb-25">{{ currentThemeTab.label }}</h5>
                <small class="text-muted">{{ currentThemeTab.description }}</small>
              </div>
            </div>
          </template>

          <b-form @submit.prevent="saveSettings">
            <b-row>
              <template v-if="isThemeType()">
                <b-col
                  v-for="key in activeThemeFields"
                  :key="key"
                  cols="12"
                  :md="getThemeFieldMd(key)"
                  :lg="getThemeFieldLg(key)"
                  class="mb-1"
                  v-show="canShowField(key)"
                >
                  <div class="theme-field-card" :class="{ 'theme-field-card--compact': activeThemeTab === 'home_cta' }">
                    <label class="font-weight-bold text-dark small text-uppercase mb-50 d-block">
                      {{ formatLabel(key) }}
                    </label>

                    <div v-if="isRichTextField(key)" class="editor-container bg-white">
                      <vue-editor v-model="settings[key]" />
                    </div>

                    <div
                      v-else-if="isThemeColorField(key)"
                      class="theme-color-field bg-white border rounded p-1"
                    >
                      <div class="d-flex align-items-center">
                        <input
                          v-model="settings[key]"
                          type="color"
                          class="theme-color-input mr-1"
                        >
                        <b-form-input
                          v-model="settings[key]"
                          class="form-control-lg"
                          :placeholder="getColorPlaceholder(key)"
                          @blur="normalizeColorValue(key)"
                        />
                      </div>
                      <small class="text-muted d-block mt-50">
                        Use hex color like #000000
                      </small>
                    </div>

                    <div
                      v-else-if="isThemeBooleanField(key)"
                      class="bg-white border rounded px-1 py-75"
                    >
                      <b-form-checkbox
                        v-model="settings[key]"
                        switch
                        value="1"
                        unchecked-value="0"
                      >
                        {{ settings[key] === '1' || settings[key] === 1 || settings[key] === true ? 'Enabled' : 'Disabled' }}
                      </b-form-checkbox>
                    </div>

                    <div
                      v-else-if="isThemeCartIconField(key)"
                      class="bg-white border rounded p-1"
                    >
                      <b-form-select
                        v-model="settings[key]"
                        :options="cartIconOptions"
                        class="form-control-lg"
                      />
                      <small class="text-muted d-block mt-50">
                        Select a preset cart icon style
                      </small>
                    </div>

                    <div
                      v-else-if="isThemeFontFamilyField(key)"
                      class="bg-white border rounded p-1"
                    >
                      <b-form-select
                        v-model="settings[key]"
                        :options="fontFamilyOptions"
                        class="form-control-lg"
                      />
                      <small class="text-muted d-block mt-50">
                        Preset font family for theme and buttons
                      </small>
                    </div>

                    <div
                      v-else-if="key === 'theme_home_cta_bg_type'"
                      class="bg-white border rounded p-1"
                    >
                      <b-form-select
                        v-model="settings[key]"
                        :options="[{text: 'Solid Color / Gradient', value: 'solid'}, {text: 'Background Image', value: 'image'}]"
                        class="form-control-lg"
                      />
                      <small class="text-muted d-block mt-50">Select background type</small>
                    </div>

                    <div
                      v-else-if="isThemeSelectField(key)"
                      class="bg-white border rounded p-1"
                    >
                      <b-form-select
                        v-model="settings[key]"
                        :options="getThemeSelectOptions(key)"
                        class="form-control-lg"
                      />
                      <small class="text-muted d-block mt-50">
                        Select a CSS preset value
                      </small>
                    </div>

                    <div
                      v-else-if="isThemeTypographyField(key)"
                      class="bg-white border rounded p-1"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon :icon="getTypographyIcon(key)" size="16" />
                        </b-input-group-prepend>
                        <b-form-input
                          v-model="settings[key]"
                          class="form-control-lg"
                          :placeholder="getTypographyPlaceholder(key)"
                        />
                      </b-input-group>
                      <small class="text-muted d-block mt-50">
                        {{ getTypographyHelpText(key) }}
                      </small>
                    </div>

                    <div v-else-if="isImageField(key)" class="bg-white p-1 border rounded">
                      <div v-if="imagePreview(key)" class="mb-1">
                        <b-img
                          :src="imagePreview(key)"
                          fluid
                          rounded
                          style="max-height: 120px;"
                        />
                      </div>

                      <b-form-file
                        :placeholder="`Choose ${formatLabel(key)}`"
                        drop-placeholder="Drop file here..."
                        @input="onFileChange($event, key)"
                      />

                      <div v-if="isMediaPickerField(key)" class="d-flex flex-wrap mt-1">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50 mb-50"
                          @click="openMediaPicker(key)"
                        >
                          <feather-icon icon="ImageIcon" size="14" class="mr-50" />
                          Choose from Media
                        </b-button>

                        <b-button
                          v-if="isMediaPickerField(key) && selectedLibraryMedia[key]"
                          size="sm"
                          variant="outline-danger"
                          class="mb-50"
                          @click="clearSelectedLibraryMedia(key)"
                        >
                          Remove Selected Media
                        </b-button>
                      </div>

                      <small class="text-muted d-block mt-50">
                        {{
                          selectedLibraryMedia[key]
                            ? `Selected media ID: ${selectedLibraryMedia[key].id}`
                            : settings[key] && typeof settings[key] === 'string'
                              ? settings[key]
                              : 'No file selected'
                        }}
                      </small>
                    </div>

                    <b-input-group v-else class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon :icon="getIcon(key)" size="16" />
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="settings[key]"
                        class="form-control-lg"
                      />
                    </b-input-group>
                  </div>
                </b-col>
              </template>

              <template v-else>
                <b-col
                  v-for="(val, key) in filteredSettings"
                  :key="key"
                  cols="12"
                  class="mb-2"
                >
                  <label class="font-weight-bold text-dark small text-uppercase mb-50 d-block">
                    {{ formatLabel(key) }}
                  </label>

                  <div v-if="isRichTextField(key)" class="editor-container bg-white">
                    <vue-editor v-model="settings[key]" />
                  </div>

                  <div v-else-if="isImageField(key)" class="bg-white p-1 border rounded">
                    <div v-if="imagePreview(key)" class="mb-1">
                      <b-img
                        :src="imagePreview(key)"
                        fluid
                        rounded
                        style="max-height: 120px;"
                      />
                    </div>

                    <b-form-file
                      :placeholder="`Choose ${formatLabel(key)}`"
                      drop-placeholder="Drop file here..."
                      @input="onFileChange($event, key)"
                    />

                    <div v-if="isMediaPickerField(key)" class="d-flex flex-wrap mt-1">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        class="mr-50 mb-50"
                        @click="openMediaPicker(key)"
                      >
                        <feather-icon icon="ImageIcon" size="14" class="mr-50" />
                        Choose from Media
                      </b-button>

                      <b-button
                        v-if="isMediaPickerField(key) && selectedLibraryMedia[key]"
                        size="sm"
                        variant="outline-danger"
                        class="mb-50"
                        @click="clearSelectedLibraryMedia(key)"
                      >
                        Remove Selected Media
                      </b-button>
                    </div>

                    <small class="text-muted d-block mt-50">
                      {{
                        selectedLibraryMedia[key]
                          ? `Selected media ID: ${selectedLibraryMedia[key].id}`
                          : settings[key] && typeof settings[key] === 'string'
                            ? settings[key]
                            : 'No file selected'
                      }}
                    </small>
                  </div>

                  <b-input-group v-else class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon :icon="getIcon(key)" size="16" />
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="settings[key]"
                      class="form-control-lg"
                    />
                  </b-input-group>
                </b-col>
              </template>

              <b-col cols="12" class="mt-3 text-right">
                <hr class="mb-2">
                <b-button
                  type="submit"
                  variant="primary"
                  class="px-4 py-1 shadow-primary font-weight-bolder"
                  :disabled="loading"
                >
                  <feather-icon v-if="!loading" icon="SaveIcon" size="16" class="mr-50" />
                  {{ loading ? 'Synchronizing...' : 'Save Changes' }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </template>

        <MediaPickerModal
          :visible="!!showMediaPickerFor"
          :multiple="false"
          :preselected="showMediaPickerFor && selectedLibraryMedia[showMediaPickerFor] ? [selectedLibraryMedia[showMediaPickerFor]] : []"
          title="Select Media"
          confirm-button-text="Use Selected Media"
          @close="showMediaPickerFor = null"
          @select="handleMediaInsert"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BForm, BFormInput,
  BButton, BSpinner, BInputGroup, BInputGroupPrepend, BImg, BFormFile,
  BFormCheckbox, BFormSelect,
} from 'bootstrap-vue'
import axios from '@/helpers/axios'
import { VueEditor } from 'vue2-editor'
import MediaPickerModal from '@/components/media/MediaPickerModal.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BSpinner,
    BInputGroup,
    BInputGroupPrepend,
    BImg,
    BFormFile,
    BFormCheckbox,
    BFormSelect,
    VueEditor,
    MediaPickerModal,
  },

  data() {
    return {
      type: this.$route.meta.type || 'general',
      title: this.$route.meta.title || 'Settings',
      settings: {},
      files: {},
      selectedLibraryMedia: {},
      showMediaPickerFor: null,
      loading: false,
      pageLoading: true,
      activeThemeTab: 'global',
      singleProductActiveSubTab: 'sorting',
      draggedSingleProductButtonKey: null,

      themeTabs: [
        { key: 'global', label: 'Global', icon: 'GlobeIcon', description: 'Primary theme color and font settings.' },
        { key: 'add_to_cart', label: 'Add to Cart', icon: 'ShoppingCartIcon', description: 'All add to cart button styles and typography.' },
        { key: 'order_now', label: 'Order Now', icon: 'ZapIcon', description: 'Order now button colors and typography.' },
        { key: 'header', label: 'Header', icon: 'LayoutIcon', description: 'Header top bar and cart icon configuration.' },
        { key: 'home_cta', label: 'Home Page CTA', icon: 'TargetIcon', description: 'Configure home page Call To Action banner.' },
        { key: 'mobile', label: 'Mobile Menu', icon: 'SmartphoneIcon', description: 'Offcanvas menu background and text color.' },
        { key: 'footer', label: 'Footer', icon: 'ArchiveIcon', description: 'Footer colors, icon colors and supporting text shades.' },
        { key: 'checkout', label: 'Checkout', icon: 'CreditCardIcon', description: 'Checkout button styles and typography.' },
        { key: 'single_product', label: 'Single Product', icon: 'ShoppingBagIcon', description: 'Single product page button controls with separate card sections and responsive button styling.' },
      ],

      themeFieldGroups: {
        global: ['theme_color', 'theme_font_family'],
        add_to_cart: [
          'theme_add_to_cart_enabled',
          'theme_add_to_cart_label',
          'theme_add_to_cart_bg',
          'theme_add_to_cart_hover_bg',
          'theme_add_to_cart_color',
          'theme_add_to_cart_padding_x_desktop',
          'theme_add_to_cart_padding_y_desktop',
          'theme_add_to_cart_padding_x_tablet',
          'theme_add_to_cart_padding_y_tablet',
          'theme_add_to_cart_padding_x_mobile',
          'theme_add_to_cart_padding_y_mobile',
          'theme_add_to_cart_font_size_desktop',
          'theme_add_to_cart_font_size_tablet',
          'theme_add_to_cart_font_size_mobile',
          'theme_add_to_cart_font_weight',
        ],
        order_now: [
          'theme_order_now_enabled',
          'theme_order_now_label',
          'theme_order_now_bg',
          'theme_order_now_hover_bg',
          'theme_order_now_color',
          'theme_order_now_padding_x_desktop',
          'theme_order_now_padding_y_desktop',
          'theme_order_now_padding_x_tablet',
          'theme_order_now_padding_y_tablet',
          'theme_order_now_padding_x_mobile',
          'theme_order_now_padding_y_mobile',
          'theme_order_now_font_size_desktop',
          'theme_order_now_font_size_tablet',
          'theme_order_now_font_size_mobile',
          'theme_order_now_font_weight',
        ],
        header: ['theme_header_top_enabled', 'theme_header_cart_icon'],
        home_cta: [
          'theme_home_cta_enabled',
          'theme_home_cta_bg_type',
          'theme_home_cta_bg_color',
          'theme_home_cta_bg_gradient',
          'theme_home_cta_bg_image',
          'theme_home_cta_bg_position',
          'theme_home_cta_bg_size',
          'theme_home_cta_bg_repeat',
          'theme_home_cta_bg_attachment',
          'theme_home_cta_overlay_color',
          'theme_home_cta_overlay_opacity',
          'theme_home_cta_min_height_desktop',
          'theme_home_cta_min_height_tablet',
          'theme_home_cta_min_height_mobile',
          'theme_home_cta_padding_top_desktop',
          'theme_home_cta_padding_bottom_desktop',
          'theme_home_cta_padding_left_desktop',
          'theme_home_cta_padding_right_desktop',
          'theme_home_cta_padding_top_tablet',
          'theme_home_cta_padding_bottom_tablet',
          'theme_home_cta_padding_left_tablet',
          'theme_home_cta_padding_right_tablet',
          'theme_home_cta_padding_top_mobile',
          'theme_home_cta_padding_bottom_mobile',
          'theme_home_cta_padding_left_mobile',
          'theme_home_cta_padding_right_mobile',
          'theme_home_cta_border_radius',
          'theme_home_cta_content_max_width',
          'theme_home_cta_content_alignment',
          'theme_home_cta_title',
          'theme_home_cta_title_color',
          'theme_home_cta_title_font_family',
          'theme_home_cta_title_font_size_desktop',
          'theme_home_cta_title_font_size_tablet',
          'theme_home_cta_title_font_size_mobile',
          'theme_home_cta_title_font_weight',
          'theme_home_cta_title_line_height',
          'theme_home_cta_title_letter_spacing',
          'theme_home_cta_title_text_transform',
          'theme_home_cta_btn_text',
          'theme_home_cta_btn_url',
          'theme_home_cta_btn_bg',
          'theme_home_cta_btn_hover_bg',
          'theme_home_cta_btn_color',
          'theme_home_cta_btn_hover_color',
          'theme_home_cta_btn_border_color',
          'theme_home_cta_btn_hover_border_color',
          'theme_home_cta_btn_border_width',
          'theme_home_cta_btn_border_radius',
          'theme_home_cta_btn_padding_x_desktop',
          'theme_home_cta_btn_padding_y_desktop',
          'theme_home_cta_btn_padding_x_tablet',
          'theme_home_cta_btn_padding_y_tablet',
          'theme_home_cta_btn_padding_x_mobile',
          'theme_home_cta_btn_padding_y_mobile',
          'theme_home_cta_btn_font_family',
          'theme_home_cta_btn_font_size_desktop',
          'theme_home_cta_btn_font_size_tablet',
          'theme_home_cta_btn_font_size_mobile',
          'theme_home_cta_btn_font_weight',
          'theme_home_cta_btn_line_height',
          'theme_home_cta_btn_letter_spacing',
          'theme_home_cta_btn_text_transform',
          'theme_home_cta_btn_box_shadow',
        ],
        mobile: ['theme_mobile_offcanvas_bg', 'theme_mobile_offcanvas_color'],
        footer: [
          'theme_footer_bg',
          'theme_footer_color',
          'theme_footer_icon_bg',
          'theme_footer_icon_border_color',
          'theme_footer_icon_color',
          'theme_footer_icon_hover_bg',
          'theme_footer_icon_hover_color',
          'theme_footer_divider_color',
          'theme_footer_muted_color',
          'theme_footer_soft_color',
          'theme_footer_address_color',
        ],
        checkout: [
          'theme_checkout_btn_bg',
          'theme_checkout_btn_color',
          'theme_checkout_btn_font_size_desktop',
          'theme_checkout_btn_font_size_tablet',
          'theme_checkout_btn_font_size_mobile',
          'theme_checkout_btn_font_weight',
        ],
        single_product: [],
      },

      cartIconOptions: [
        { text: 'Default Cart', value: 'default' },
        { text: 'Cart Style 2', value: 'cart-2' },
        { text: 'Cart Style 3', value: 'cart-3' },
      ],

      fontFamilyOptions: [
        { text: 'Default Theme Font', value: '' },
        { text: 'Hind Siliguri', value: "'Hind Siliguri', sans-serif" },
        { text: 'Inter', value: "Inter, sans-serif" },
        { text: 'Open Sans', value: "'Open Sans', sans-serif" },
        { text: 'Poppins', value: "Poppins, sans-serif" },
        { text: 'Roboto', value: "Roboto, sans-serif" },
        { text: 'Arial', value: "Arial, sans-serif" },
      ],

      defaultThemeKeys: {
        theme_color: '#800277',
        theme_font_family: '',
        theme_add_to_cart_enabled: '1',
        theme_add_to_cart_label: 'ADD TO CART',
        theme_add_to_cart_bg: '#000000',
        theme_add_to_cart_hover_bg: '#333333',
        theme_add_to_cart_color: '#ffffff',
        theme_add_to_cart_padding_x_desktop: '22px',
        theme_add_to_cart_padding_y_desktop: '12px',
        theme_add_to_cart_padding_x_tablet: '20px',
        theme_add_to_cart_padding_y_tablet: '11px',
        theme_add_to_cart_padding_x_mobile: '16px',
        theme_add_to_cart_padding_y_mobile: '10px',
        theme_add_to_cart_font_size_desktop: '16px',
        theme_add_to_cart_font_size_tablet: '15px',
        theme_add_to_cart_font_size_mobile: '14px',
        theme_add_to_cart_font_weight: '600',
        theme_order_now_enabled: '1',
        theme_order_now_label: 'অর্ডার করুন',
        theme_order_now_bg: '#f59e0b',
        theme_order_now_hover_bg: '#d97706',
        theme_order_now_color: '#ffffff',
        theme_order_now_padding_x_desktop: '22px',
        theme_order_now_padding_y_desktop: '12px',
        theme_order_now_padding_x_tablet: '20px',
        theme_order_now_padding_y_tablet: '11px',
        theme_order_now_padding_x_mobile: '16px',
        theme_order_now_padding_y_mobile: '10px',
        theme_order_now_font_size_desktop: '16px',
        theme_order_now_font_size_tablet: '15px',
        theme_order_now_font_size_mobile: '14px',
        theme_order_now_font_weight: '600',
        theme_header_top_enabled: '1',
        theme_header_cart_icon: 'default',
        
        // --- NEW HOME CTA DEFAULT KEYS ---
        theme_home_cta_enabled: '1',
        theme_home_cta_bg_type: 'solid',
        theme_home_cta_bg_color: '#222529',
        theme_home_cta_bg_gradient: 'linear-gradient(135deg, #222529 0%, #111827 100%)',
        theme_home_cta_bg_image: '',
        theme_home_cta_bg_position: 'center center',
        theme_home_cta_bg_size: 'cover',
        theme_home_cta_bg_repeat: 'no-repeat',
        theme_home_cta_bg_attachment: 'scroll',
        theme_home_cta_overlay_color: '#000000',
        theme_home_cta_overlay_opacity: '0',
        theme_home_cta_min_height_desktop: '180px',
        theme_home_cta_min_height_tablet: '160px',
        theme_home_cta_min_height_mobile: '140px',
        theme_home_cta_padding_top_desktop: '48px',
        theme_home_cta_padding_bottom_desktop: '48px',
        theme_home_cta_padding_left_desktop: '32px',
        theme_home_cta_padding_right_desktop: '32px',
        theme_home_cta_padding_top_tablet: '40px',
        theme_home_cta_padding_bottom_tablet: '40px',
        theme_home_cta_padding_left_tablet: '24px',
        theme_home_cta_padding_right_tablet: '24px',
        theme_home_cta_padding_top_mobile: '32px',
        theme_home_cta_padding_bottom_mobile: '32px',
        theme_home_cta_padding_left_mobile: '16px',
        theme_home_cta_padding_right_mobile: '16px',
        theme_home_cta_border_radius: '0px',
        theme_home_cta_content_max_width: '720px',
        theme_home_cta_content_alignment: 'center',
        theme_home_cta_title: 'The Lowest Prices Once A Month! Hurry To Snap Up',
        theme_home_cta_title_color: '#ffffff',
        theme_home_cta_title_font_family: '',
        theme_home_cta_title_font_size_desktop: '24px',
        theme_home_cta_title_font_size_tablet: '20px',
        theme_home_cta_title_font_size_mobile: '18px',
        theme_home_cta_title_font_weight: '600',
        theme_home_cta_title_line_height: '1.3',
        theme_home_cta_title_letter_spacing: '0px',
        theme_home_cta_title_text_transform: 'none',
        theme_home_cta_btn_text: 'SHOP NOW!',
        theme_home_cta_btn_url: '#',
        theme_home_cta_btn_bg: 'transparent',
        theme_home_cta_btn_hover_bg: '#ffffff',
        theme_home_cta_btn_color: '#ffffff',
        theme_home_cta_btn_hover_color: '#000000',
        theme_home_cta_btn_border_color: '#ffffff',
        theme_home_cta_btn_hover_border_color: '#ffffff',
        theme_home_cta_btn_border_width: '1px',
        theme_home_cta_btn_border_radius: '0px',
        theme_home_cta_btn_padding_x_desktop: '24px',
        theme_home_cta_btn_padding_y_desktop: '12px',
        theme_home_cta_btn_padding_x_tablet: '22px',
        theme_home_cta_btn_padding_y_tablet: '11px',
        theme_home_cta_btn_padding_x_mobile: '20px',
        theme_home_cta_btn_padding_y_mobile: '10px',
        theme_home_cta_btn_font_family: '',
        theme_home_cta_btn_font_size_desktop: '14px',
        theme_home_cta_btn_font_size_tablet: '13px',
        theme_home_cta_btn_font_size_mobile: '12px',
        theme_home_cta_btn_font_weight: '600',
        theme_home_cta_btn_line_height: '1.2',
        theme_home_cta_btn_letter_spacing: '0px',
        theme_home_cta_btn_text_transform: 'uppercase',
        theme_home_cta_btn_box_shadow: 'none',
        // ---------------------------------

        theme_mobile_offcanvas_bg: '#222529',
        theme_mobile_offcanvas_color: '#ffffff',
        theme_footer_bg: '#000000',
        theme_footer_color: '#ffffff',
        theme_footer_icon_bg: '#111111',
        theme_footer_icon_border_color: '#333333',
        theme_footer_icon_color: '#ffffff',
        theme_footer_icon_hover_bg: '#ffffff',
        theme_footer_icon_hover_color: '#000000',
        theme_footer_divider_color: '#222222',
        theme_footer_muted_color: '#999999',
        theme_footer_soft_color: '#888888',
        theme_footer_address_color: '#555555',
        theme_checkout_btn_bg: '#2563eb',
        theme_checkout_btn_color: '#ffffff',
        theme_checkout_btn_font_size_desktop: '17px',
        theme_checkout_btn_font_size_tablet: '16px',
        theme_checkout_btn_font_size_mobile: '15px',
        theme_checkout_btn_font_weight: '700',

        single_product_button_order: '["add_to_cart","order_now","call","whatsapp","messenger"]',
        single_product_add_to_cart_enabled: '1',
        single_product_add_to_cart_label: 'কার্টে যোগ করুন',
        single_product_add_to_cart_sort_order: '1',
        single_product_add_to_cart_bg: '#222529',
        single_product_add_to_cart_hover_bg: '#000000',
        single_product_add_to_cart_color: '#ffffff',
        single_product_add_to_cart_hover_color: '#ffffff',
        single_product_add_to_cart_border_color: '#222529',
        single_product_add_to_cart_hover_border_color: '#000000',
        single_product_add_to_cart_border_width: '1px',
        single_product_add_to_cart_radius: '4px',
        single_product_add_to_cart_padding_x_desktop: '22px',
        single_product_add_to_cart_padding_y_desktop: '12px',
        single_product_add_to_cart_padding_x_tablet: '20px',
        single_product_add_to_cart_padding_y_tablet: '11px',
        single_product_add_to_cart_padding_x_mobile: '16px',
        single_product_add_to_cart_padding_y_mobile: '10px',
        single_product_add_to_cart_font_family: '',
        single_product_add_to_cart_font_size_desktop: '16px',
        single_product_add_to_cart_font_size_tablet: '15px',
        single_product_add_to_cart_font_size_mobile: '14px',
        single_product_add_to_cart_font_weight: '600',
        single_product_add_to_cart_line_height: '1.2',
        single_product_add_to_cart_letter_spacing: '0px',
        single_product_add_to_cart_text_transform: 'none',

        single_product_order_now_enabled: '1',
        single_product_order_now_label: 'অর্ডার করুন',
        single_product_order_now_sort_order: '2',
        single_product_order_now_bg: '#ffc107',
        single_product_order_now_hover_bg: '#e0a800',
        single_product_order_now_color: '#000000',
        single_product_order_now_hover_color: '#000000',
        single_product_order_now_border_color: '#ffc107',
        single_product_order_now_hover_border_color: '#e0a800',
        single_product_order_now_border_width: '1px',
        single_product_order_now_radius: '4px',
        single_product_order_now_padding_x_desktop: '22px',
        single_product_order_now_padding_y_desktop: '12px',
        single_product_order_now_padding_x_tablet: '20px',
        single_product_order_now_padding_y_tablet: '11px',
        single_product_order_now_padding_x_mobile: '16px',
        single_product_order_now_padding_y_mobile: '10px',
        single_product_order_now_font_family: '',
        single_product_order_now_font_size_desktop: '16px',
        single_product_order_now_font_size_tablet: '15px',
        single_product_order_now_font_size_mobile: '14px',
        single_product_order_now_font_weight: '600',
        single_product_order_now_line_height: '1.2',
        single_product_order_now_letter_spacing: '0px',
        single_product_order_now_text_transform: 'none',

        single_product_call_enabled: '1',
        single_product_call_label: 'Call Now',
        single_product_call_sort_order: '3',
        single_product_call_number: '',
        single_product_call_bg: '#10b981',
        single_product_call_hover_bg: '#059669',
        single_product_call_color: '#ffffff',
        single_product_call_hover_color: '#ffffff',
        single_product_call_border_color: '#10b981',
        single_product_call_hover_border_color: '#059669',
        single_product_call_border_width: '1px',
        single_product_call_radius: '4px',
        single_product_call_padding_x_desktop: '22px',
        single_product_call_padding_y_desktop: '12px',
        single_product_call_padding_x_tablet: '20px',
        single_product_call_padding_y_tablet: '11px',
        single_product_call_padding_x_mobile: '16px',
        single_product_call_padding_y_mobile: '10px',
        single_product_call_font_family: '',
        single_product_call_font_size_desktop: '16px',
        single_product_call_font_size_tablet: '15px',
        single_product_call_font_size_mobile: '14px',
        single_product_call_font_weight: '600',
        single_product_call_line_height: '1.2',
        single_product_call_letter_spacing: '0px',
        single_product_call_text_transform: 'none',

        single_product_whatsapp_enabled: '1',
        single_product_whatsapp_label: 'WhatsApp এ অর্ডার করুন',
        single_product_whatsapp_sort_order: '4',
        single_product_whatsapp_bg: '#25d366',
        single_product_whatsapp_hover_bg: '#1ebe5d',
        single_product_whatsapp_color: '#ffffff',
        single_product_whatsapp_hover_color: '#ffffff',
        single_product_whatsapp_border_color: '#25d366',
        single_product_whatsapp_hover_border_color: '#1ebe5d',
        single_product_whatsapp_border_width: '1px',
        single_product_whatsapp_radius: '4px',
        single_product_whatsapp_padding_x_desktop: '22px',
        single_product_whatsapp_padding_y_desktop: '12px',
        single_product_whatsapp_padding_x_tablet: '20px',
        single_product_whatsapp_padding_y_tablet: '11px',
        single_product_whatsapp_padding_x_mobile: '16px',
        single_product_whatsapp_padding_y_mobile: '10px',
        single_product_whatsapp_font_family: '',
        single_product_whatsapp_font_size_desktop: '16px',
        single_product_whatsapp_font_size_tablet: '15px',
        single_product_whatsapp_font_size_mobile: '14px',
        single_product_whatsapp_font_weight: '600',
        single_product_whatsapp_line_height: '1.2',
        single_product_whatsapp_letter_spacing: '0px',
        single_product_whatsapp_text_transform: 'none',

        single_product_messenger_enabled: '1',
        single_product_messenger_label: 'Messenger এ অর্ডার করুন',
        single_product_messenger_sort_order: '5',
        single_product_messenger_bg: '#8a3ffc',
        single_product_messenger_hover_bg: '#722ad8',
        single_product_messenger_color: '#ffffff',
        single_product_messenger_hover_color: '#ffffff',
        single_product_messenger_border_color: '#8a3ffc',
        single_product_messenger_hover_border_color: '#722ad8',
        single_product_messenger_border_width: '1px',
        single_product_messenger_radius: '4px',
        single_product_messenger_padding_x_desktop: '22px',
        single_product_messenger_padding_y_desktop: '12px',
        single_product_messenger_padding_x_tablet: '20px',
        single_product_messenger_padding_y_tablet: '11px',
        single_product_messenger_padding_x_mobile: '16px',
        single_product_messenger_padding_y_mobile: '10px',
        single_product_messenger_font_family: '',
        single_product_messenger_font_size_desktop: '16px',
        single_product_messenger_font_size_tablet: '15px',
        single_product_messenger_font_size_mobile: '14px',
        single_product_messenger_font_weight: '600',
        single_product_messenger_line_height: '1.2',
        single_product_messenger_letter_spacing: '0px',
        single_product_messenger_text_transform: 'none',
      },
    }
  },

  computed: {
    filteredSettings() {
      const { slug } = this.$route.params
      if (!slug) return this.settings
      const filtered = {}
      if (this.settings[slug] !== undefined) filtered[slug] = this.settings[slug]
      return filtered
    },

    currentThemeTab() {
      return this.themeTabs.find(tab => tab.key === this.activeThemeTab) || this.themeTabs[0]
    },

    activeThemeFields() {
      if (!this.isThemeType()) return []
      return this.themeFieldGroups[this.activeThemeTab] || []
    },
    singleProductSubTabs() {
      return [
        { key: 'sorting', label: 'Button Order', icon: 'MoveIcon' },
        ...this.orderedSingleProductButtonCards.map(card => ({
          key: card.key,
          label: card.title,
          icon: 'SquareIcon',
        })),
      ]
    },
    orderedSingleProductButtonCards() {
      const cards = this.singleProductButtonCards
      const raw = this.settings.single_product_button_order
      let orderedKeys = []
      try {
        orderedKeys = Array.isArray(raw) ? raw : JSON.parse(raw || '[]')
      } catch (e) {
        orderedKeys = []
      }
      const fallback = cards.map(card => card.key)
      const finalKeys = [
        ...orderedKeys.filter(key => fallback.includes(key)),
        ...fallback.filter(key => !orderedKeys.includes(key)),
      ]
      return finalKeys
        .map(key => cards.find(card => card.key === key))
        .filter(Boolean)
    },
    singleProductButtonCards() {
      return [
        {
          key: 'add_to_cart',
          title: 'Add to Cart Button',
          description: 'Primary cart button shown on the single product page and sticky bar.',
          fields: [
            'single_product_add_to_cart_enabled',
            'single_product_add_to_cart_label',
            'single_product_add_to_cart_sort_order',
            'single_product_add_to_cart_bg',
            'single_product_add_to_cart_hover_bg',
            'single_product_add_to_cart_color',
            'single_product_add_to_cart_hover_color',
            'single_product_add_to_cart_border_color',
            'single_product_add_to_cart_hover_border_color',
            'single_product_add_to_cart_border_width',
            'single_product_add_to_cart_radius',
            'single_product_add_to_cart_padding_x_desktop',
            'single_product_add_to_cart_padding_y_desktop',
            'single_product_add_to_cart_padding_x_tablet',
            'single_product_add_to_cart_padding_y_tablet',
            'single_product_add_to_cart_padding_x_mobile',
            'single_product_add_to_cart_padding_y_mobile',
            'single_product_add_to_cart_font_family',
            'single_product_add_to_cart_font_size_desktop',
            'single_product_add_to_cart_font_size_tablet',
            'single_product_add_to_cart_font_size_mobile',
            'single_product_add_to_cart_font_weight',
            'single_product_add_to_cart_line_height',
            'single_product_add_to_cart_letter_spacing',
            'single_product_add_to_cart_text_transform',
          ],
        },
        {
          key: 'order_now',
          title: 'Order Now Button',
          description: 'Quick order button shown on the single product page and sticky bar.',
          fields: [
            'single_product_order_now_enabled',
            'single_product_order_now_label',
            'single_product_order_now_sort_order',
            'single_product_order_now_bg',
            'single_product_order_now_hover_bg',
            'single_product_order_now_color',
            'single_product_order_now_hover_color',
            'single_product_order_now_border_color',
            'single_product_order_now_hover_border_color',
            'single_product_order_now_border_width',
            'single_product_order_now_radius',
            'single_product_order_now_padding_x_desktop',
            'single_product_order_now_padding_y_desktop',
            'single_product_order_now_padding_x_tablet',
            'single_product_order_now_padding_y_tablet',
            'single_product_order_now_padding_x_mobile',
            'single_product_order_now_padding_y_mobile',
            'single_product_order_now_font_family',
            'single_product_order_now_font_size_desktop',
            'single_product_order_now_font_size_tablet',
            'single_product_order_now_font_size_mobile',
            'single_product_order_now_font_weight',
            'single_product_order_now_line_height',
            'single_product_order_now_letter_spacing',
            'single_product_order_now_text_transform',
          ],
        },
        {
          key: 'call',
          title: 'Call Button',
          description: 'Call button styles, label and phone number with country code.',
          fields: [
            'single_product_call_enabled',
            'single_product_call_label',
            'single_product_call_sort_order',
            'single_product_call_number',
            'single_product_call_bg',
            'single_product_call_hover_bg',
            'single_product_call_color',
            'single_product_call_hover_color',
            'single_product_call_border_color',
            'single_product_call_hover_border_color',
            'single_product_call_border_width',
            'single_product_call_radius',
            'single_product_call_padding_x_desktop',
            'single_product_call_padding_y_desktop',
            'single_product_call_padding_x_tablet',
            'single_product_call_padding_y_tablet',
            'single_product_call_padding_x_mobile',
            'single_product_call_padding_y_mobile',
            'single_product_call_font_family',
            'single_product_call_font_size_desktop',
            'single_product_call_font_size_tablet',
            'single_product_call_font_size_mobile',
            'single_product_call_font_weight',
            'single_product_call_line_height',
            'single_product_call_letter_spacing',
            'single_product_call_text_transform',
          ],
        },
        {
          key: 'whatsapp',
          title: 'WhatsApp Button',
          description: 'WhatsApp order button styles and label controls.',
          fields: [
            'single_product_whatsapp_enabled',
            'single_product_whatsapp_label',
            'single_product_whatsapp_sort_order',
            'single_product_whatsapp_bg',
            'single_product_whatsapp_hover_bg',
            'single_product_whatsapp_color',
            'single_product_whatsapp_hover_color',
            'single_product_whatsapp_border_color',
            'single_product_whatsapp_hover_border_color',
            'single_product_whatsapp_border_width',
            'single_product_whatsapp_radius',
            'single_product_whatsapp_padding_x_desktop',
            'single_product_whatsapp_padding_y_desktop',
            'single_product_whatsapp_padding_x_tablet',
            'single_product_whatsapp_padding_y_tablet',
            'single_product_whatsapp_padding_x_mobile',
            'single_product_whatsapp_padding_y_mobile',
            'single_product_whatsapp_font_family',
            'single_product_whatsapp_font_size_desktop',
            'single_product_whatsapp_font_size_tablet',
            'single_product_whatsapp_font_size_mobile',
            'single_product_whatsapp_font_weight',
            'single_product_whatsapp_line_height',
            'single_product_whatsapp_letter_spacing',
            'single_product_whatsapp_text_transform',
          ],
        },
        {
          key: 'messenger',
          title: 'Messenger Button',
          description: 'Messenger order button styles and label controls.',
          fields: [
            'single_product_messenger_enabled',
            'single_product_messenger_label',
            'single_product_messenger_sort_order',
            'single_product_messenger_bg',
            'single_product_messenger_hover_bg',
            'single_product_messenger_color',
            'single_product_messenger_hover_color',
            'single_product_messenger_border_color',
            'single_product_messenger_hover_border_color',
            'single_product_messenger_border_width',
            'single_product_messenger_radius',
            'single_product_messenger_padding_x_desktop',
            'single_product_messenger_padding_y_desktop',
            'single_product_messenger_padding_x_tablet',
            'single_product_messenger_padding_y_tablet',
            'single_product_messenger_padding_x_mobile',
            'single_product_messenger_padding_y_mobile',
            'single_product_messenger_font_family',
            'single_product_messenger_font_size_desktop',
            'single_product_messenger_font_size_tablet',
            'single_product_messenger_font_size_mobile',
            'single_product_messenger_font_weight',
            'single_product_messenger_line_height',
            'single_product_messenger_letter_spacing',
            'single_product_messenger_text_transform',
          ],
        },
      ]
    },
  },

  watch: {
    '$route.params.slug'() {
      this.fetchSettings()
    },
    '$route.meta.type'(newType) {
      this.type = newType || 'general'
      if (this.isThemeType() && !this.themeFieldGroups[this.activeThemeTab]) this.activeThemeTab = 'global'
      this.fetchSettings()
    },
  },

  mounted() {
    this.fetchSettings()
  },

  methods: {
    canShowField(key) {
      // Background config specific visibility controls
      if (key === 'theme_home_cta_bg_color' && this.settings.theme_home_cta_bg_type === 'image') return false;
      if (key === 'theme_home_cta_bg_gradient' && this.settings.theme_home_cta_bg_type === 'image') return false;
      if (key === 'theme_home_cta_bg_image' && this.settings.theme_home_cta_bg_type === 'solid') return false;
      if ([
        'theme_home_cta_bg_position',
        'theme_home_cta_bg_size',
        'theme_home_cta_bg_repeat',
        'theme_home_cta_bg_attachment',
      ].includes(key) && this.settings.theme_home_cta_bg_type !== 'image') return false;
      return true;
    },

    isRichTextField(key) {
      if (this.isThemeType() && String(key || '').startsWith('theme_home_cta_')) return false
      return String(key || '').includes('content')
    },

    getThemeFieldMd(key) {
      if (this.activeThemeTab !== 'home_cta') return 12
      if (['theme_home_cta_title', 'theme_home_cta_btn_url', 'theme_home_cta_bg_gradient', 'theme_home_cta_bg_image'].includes(key)) return 12
      return 6
    },

    getThemeFieldLg(key) {
      if (this.activeThemeTab !== 'home_cta') return 12
      if (['theme_home_cta_title', 'theme_home_cta_btn_url', 'theme_home_cta_bg_gradient', 'theme_home_cta_bg_image'].includes(key)) return 6
      return 4
    },

    formatLabel(key) {
      return key
        .replace('theme_home_cta_', 'CTA ')
        .replace(/_url|_link|_name|_text|site_|content_/g, '')
        .replace(/_/g, ' ')
        .toUpperCase()
    },

    getIcon(key) {
      if (key.includes('whatsapp')) return 'MessageCircleIcon'
      if (key.includes('facebook') || key.includes('messenger')) return 'FacebookIcon'
      if (this.isImageField(key)) return 'ImageIcon'
      if (this.isThemeBooleanField(key)) return 'ToggleRightIcon'
      if (this.isThemeColorField(key)) return 'DropletIcon'
      if (this.isThemeTypographyField(key)) return this.getTypographyIcon(key)
      return 'FileTextIcon'
    },

    getTypographyIcon(key) {
      if (key.includes('font_weight')) return 'BoldIcon'
      if (key.includes('desktop')) return 'MonitorIcon'
      if (key.includes('tablet')) return 'TabletIcon'
      if (key.includes('mobile')) return 'SmartphoneIcon'
      return 'TypeIcon'
    },

    getTypographyPlaceholder(key) {
      if (key.includes('font_weight')) return 'e.g. 600'
      if (key.includes('line_height')) return 'e.g. 1.3'
      if (key.includes('letter_spacing')) return 'e.g. 0px'
      return 'e.g. 16px'
    },

    getTypographyHelpText(key) {
      if (key.includes('font_weight')) return 'Use font weight like 400, 500, 600, 700'
      if (key.includes('line_height')) return 'Use unitless or CSS line-height value, e.g. 1.3 or 24px'
      if (key.includes('letter_spacing')) return 'Use CSS letter spacing value, e.g. 0px, 1px, 0.04em'
      if (key.includes('desktop')) return 'Desktop typography value, e.g. 16px'
      if (key.includes('tablet')) return 'Tablet typography value, e.g. 15px'
      if (key.includes('mobile')) return 'Mobile typography value, e.g. 14px'
      return 'Typography value'
    },

    isThemeType() {
      return this.type === 'theme' || this.$route.meta.type === 'theme'
    },

    isThemeColorField(key) {
      if (!this.isThemeType()) return false
      if (String(key).startsWith('single_product_') && ['_bg', '_hover_bg', '_color', '_hover_color', '_border_color', '_hover_border_color'].some(suffix => String(key).endsWith(suffix))) {
        return true
      }
      return [
        'theme_color',
        'theme_add_to_cart_bg',
        'theme_add_to_cart_hover_bg',
        'theme_add_to_cart_color',
        'theme_order_now_bg',
        'theme_order_now_hover_bg',
        'theme_order_now_color',
        'theme_mobile_offcanvas_bg',
        'theme_mobile_offcanvas_color',
        'theme_footer_bg',
        'theme_footer_color',
        'theme_footer_icon_bg',
        'theme_footer_icon_border_color',
        'theme_footer_icon_color',
        'theme_footer_icon_hover_bg',
        'theme_footer_icon_hover_color',
        'theme_footer_divider_color',
        'theme_footer_muted_color',
        'theme_footer_soft_color',
        'theme_footer_address_color',
        'theme_checkout_btn_bg',
        'theme_checkout_btn_color',
        'theme_home_cta_bg_color',
        'theme_home_cta_overlay_color',
        'theme_home_cta_title_color',
        'theme_home_cta_btn_bg',
        'theme_home_cta_btn_hover_bg',
        'theme_home_cta_btn_color',
        'theme_home_cta_btn_hover_color',
        'theme_home_cta_btn_border_color',
        'theme_home_cta_btn_hover_border_color',
      ].includes(key)
    },

    isThemeBooleanField(key) {
      if (!this.isThemeType()) return false
      return key === 'theme_header_top_enabled'
        || key === 'theme_add_to_cart_enabled'
        || key === 'theme_order_now_enabled'
        || key === 'theme_home_cta_enabled'
        || (String(key).startsWith('single_product_') && String(key).endsWith('_enabled'))
    },

    isThemeCartIconField(key) {
      return this.isThemeType() && key === 'theme_header_cart_icon'
    },

    isThemeFontFamilyField(key) {
      return this.isThemeType() && (
        key === 'theme_font_family'
        || (String(key).startsWith('single_product_') && String(key).endsWith('_font_family'))
        || (String(key).startsWith('theme_home_cta_') && String(key).endsWith('_font_family'))
      )
    },

    isThemeSelectField(key) {
      if (!this.isThemeType()) return false
      return [
        'theme_home_cta_bg_position',
        'theme_home_cta_bg_size',
        'theme_home_cta_bg_repeat',
        'theme_home_cta_bg_attachment',
        'theme_home_cta_content_alignment',
        'theme_home_cta_title_text_transform',
        'theme_home_cta_btn_text_transform',
      ].includes(key)
    },

    getThemeSelectOptions(key) {
      const optionMap = {
        theme_home_cta_bg_position: [
          { text: 'Center Center', value: 'center center' },
          { text: 'Center Top', value: 'center top' },
          { text: 'Center Bottom', value: 'center bottom' },
          { text: 'Left Center', value: 'left center' },
          { text: 'Right Center', value: 'right center' },
        ],
        theme_home_cta_bg_size: [
          { text: 'Cover', value: 'cover' },
          { text: 'Contain', value: 'contain' },
          { text: 'Auto', value: 'auto' },
          { text: '100% Auto', value: '100% auto' },
          { text: '100% 100%', value: '100% 100%' },
        ],
        theme_home_cta_bg_repeat: [
          { text: 'No Repeat', value: 'no-repeat' },
          { text: 'Repeat', value: 'repeat' },
          { text: 'Repeat X', value: 'repeat-x' },
          { text: 'Repeat Y', value: 'repeat-y' },
        ],
        theme_home_cta_bg_attachment: [
          { text: 'Scroll', value: 'scroll' },
          { text: 'Fixed', value: 'fixed' },
          { text: 'Local', value: 'local' },
        ],
        theme_home_cta_content_alignment: [
          { text: 'Left', value: 'left' },
          { text: 'Center', value: 'center' },
          { text: 'Right', value: 'right' },
        ],
        theme_home_cta_title_text_transform: [
          { text: 'None', value: 'none' },
          { text: 'Uppercase', value: 'uppercase' },
          { text: 'Lowercase', value: 'lowercase' },
          { text: 'Capitalize', value: 'capitalize' },
        ],
        theme_home_cta_btn_text_transform: [
          { text: 'None', value: 'none' },
          { text: 'Uppercase', value: 'uppercase' },
          { text: 'Lowercase', value: 'lowercase' },
          { text: 'Capitalize', value: 'capitalize' },
        ],
      }
      return optionMap[key] || []
    },

    isThemeTypographyField(key) {
      return this.isThemeType() && (
        key.includes('_font_size_')
        || key.endsWith('_font_weight')
        || key.endsWith('_line_height')
        || key.endsWith('_letter_spacing')
      )
    },

    isImageField(key) {
      if (this.isThemeColorField(key) || this.isThemeCartIconField(key) || this.isThemeFontFamilyField(key) || this.isThemeSelectField(key) || this.isThemeTypographyField(key)) {
        return false
      }

      const normalized = String(key || '').toLowerCase()
      const imageKeys = ['logo', 'favicon', 'banner', 'image', 'thumbnail']

      if (imageKeys.some(item => normalized.includes(item))) return true
      return normalized.includes('icon') && normalized.includes('upload')
    },

    isMediaPickerField(key) {
      const normalized = String(key || '').toLowerCase()
      return normalized === 'logo'
        || normalized === 'site_logo'
        || normalized === 'favicon'
        || normalized === 'theme_home_cta_bg_image'
        || normalized.endsWith('_logo')
        || normalized.endsWith('_favicon')
    },

    getColorPlaceholder() {
      return '#000000'
    },

    normalizeColorValue(key) {
      if (!this.isThemeColorField(key)) return
      let value = String(this.settings[key] || '').trim()

      if (!value) {
        this.$set(this.settings, key, this.defaultThemeKeys[key] || '#000000')
        return
      }

      if (!value.startsWith('#')) value = `#${value}`

      const valid = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)
      this.$set(this.settings, key, valid ? value : (this.defaultThemeKeys[key] || '#000000'))
    },


    setSingleProductSubTab(tabKey) {
      this.singleProductActiveSubTab = tabKey
    },

    getSingleProductButtonOrderList() {
      const fallback = this.singleProductButtonCards.map(card => card.key)
      const raw = this.settings.single_product_button_order
      let order = []
      try {
        order = Array.isArray(raw) ? raw : JSON.parse(raw || '[]')
      } catch (e) {
        order = []
      }
      const finalOrder = [
        ...order.filter(key => fallback.includes(key)),
        ...fallback.filter(key => !order.includes(key)),
      ]
      this.$set(this.settings, 'single_product_button_order', JSON.stringify(finalOrder))
      finalOrder.forEach((key, index) => {
        this.$set(this.settings, `single_product_${key}_sort_order`, String(index + 1))
      })
      return finalOrder
    },

    handleSingleProductDragStart(cardKey) {
      this.draggedSingleProductButtonKey = cardKey
    },

    handleSingleProductDrop(targetKey) {
      if (!this.draggedSingleProductButtonKey || this.draggedSingleProductButtonKey === targetKey) return
      const order = this.getSingleProductButtonOrderList()
      const fromIndex = order.indexOf(this.draggedSingleProductButtonKey)
      const toIndex = order.indexOf(targetKey)
      if (fromIndex === -1 || toIndex === -1) return
      const next = [...order]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      this.$set(this.settings, 'single_product_button_order', JSON.stringify(next))
      next.forEach((key, index) => {
        this.$set(this.settings, `single_product_${key}_sort_order`, String(index + 1))
      })
      this.draggedSingleProductButtonKey = null
    },

    handleSingleProductDragEnd() {
      this.draggedSingleProductButtonKey = null
    },

    imagePreview(key) {
      if (this.files[key] instanceof File) return URL.createObjectURL(this.files[key])
      if (this.selectedLibraryMedia[key]) return this.selectedLibraryMedia[key].preview_url || this.selectedLibraryMedia[key].original_url
      if (typeof this.settings[key] === 'string' && this.settings[key]) return this.settings[key]
      return null
    },

    onFileChange(file, key) {
      if (!file) return
      this.$set(this.files, key, file)
      this.$set(this.selectedLibraryMedia, key, null)
    },

    openMediaPicker(key) {
      this.showMediaPickerFor = key
    },

    handleMediaInsert(items) {
      const key = this.showMediaPickerFor
      const item = Array.isArray(items) ? (items[0] || null) : items
      if (!key || !item) {
        this.showMediaPickerFor = null
        return
      }
      const mediaUrl = item.original_url
        || item.preview_url
        || item.url
        || item.full_url
        || item.path
        || ''

      this.$set(this.selectedLibraryMedia, key, item)
      this.$set(this.files, key, null)

      if (mediaUrl) {
        this.$set(this.settings, key, mediaUrl)
      }

      this.showMediaPickerFor = null
    },

    clearSelectedLibraryMedia(key) {
      this.$set(this.selectedLibraryMedia, key, null)
      if (this.isMediaPickerField(key)) {
        this.$set(this.settings, key, '')
        this.$set(this.files, key, null)
      }
    },

    injectDefaultThemeFields() {
      Object.keys(this.defaultThemeKeys).forEach(key => {
        if (this.settings[key] === undefined) this.$set(this.settings, key, this.defaultThemeKeys[key])
      })
    },

    async fetchSettings() {
      this.pageLoading = true
      this.files = {}
      this.selectedLibraryMedia = {}
      this.showMediaPickerFor = null

      const { slug } = this.$route.params
      const fetchType = this.$route.meta.type || 'pages'

      try {
        const res = await axios.get(`/api/v1/admin/business-settings/${fetchType}`)
        this.settings = { ...res.data }

        if (fetchType === 'social') {
          if (this.settings.whatsapp_number === undefined) this.$set(this.settings, 'whatsapp_number', '')
          if (this.settings.messenger_link === undefined) this.$set(this.settings, 'messenger_link', '')
        }

        if (fetchType === 'theme') {
          this.injectDefaultThemeFields()
          if (!this.themeFieldGroups[this.activeThemeTab]) this.activeThemeTab = 'global'
        }

        if (slug && this.settings[slug] === undefined) this.$set(this.settings, slug, '')
      } catch (e) {
        console.error('Fetch Error:', e)
      } finally {
        this.pageLoading = false
      }
    },

    async saveSettings() {
      this.loading = true
      const formData = new FormData()
      formData.append('type', this.type)

      Object.keys(this.settings).forEach(key => {
        if (this.isThemeColorField(key)) this.normalizeColorValue(key)

        if (this.files[key] instanceof File) {
          formData.append(key, this.files[key])
        } else {
          formData.append(key, this.settings[key] ?? '')
        }

        if (this.selectedLibraryMedia[key] && this.selectedLibraryMedia[key].id) {
          formData.append(`${key}_media_id`, this.selectedLibraryMedia[key].id)
        }
      })

      try {
        await axios.post('/api/v1/admin/business-settings/update', formData)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Settings Updated Successfully',
          },
        })
        this.fetchSettings()
      } catch (e) {
        console.error('Update Failed:', e)
        alert('Update Failed')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.bg-light-primary { background: #f8f8fb !important; }
.editor-container { min-height: 300px; border-radius: 8px; overflow: hidden; }
.shadow-primary { box-shadow: 0 8px 25px -8px rgba(115, 103, 240, 0.4) !important; }
::v-deep .ql-editor { min-height: 400px; font-size: 16px; }

.theme-color-field { border-radius: 8px; }

.theme-color-input {
  width: 48px;
  min-width: 48px;
  height: 48px;
  padding: 0;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  background: transparent;
  overflow: hidden;
}

.theme-tabs-wrap {
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  padding: 10px;
}

.theme-tabs-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: thin;
}

.theme-tab-btn {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.theme-tab-btn.active {
  background: linear-gradient(135deg, #7367f0, #5f54d8);
  color: #ffffff;
  border-color: #7367f0;
  box-shadow: 0 8px 18px -8px rgba(115, 103, 240, 0.65);
}

.theme-section-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08), rgba(115, 103, 240, 0.02));
  border: 1px solid rgba(115, 103, 240, 0.12);
  border-radius: 14px;
  padding: 16px 18px;
}

.theme-field-card {
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  padding: 14px;
}

.theme-field-card--nested {
  height: 100%;
  padding: 12px;
}

.theme-button-card {
  background: #ffffff;
  border: 1px solid #e9e7ff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px -20px rgba(115, 103, 240, 0.35);
}


.theme-subtabs-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  padding: 12px;
}

.theme-subtab-btn {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.theme-subtab-btn.active {
  background: linear-gradient(135deg, #7367f0, #5f54d8);
  color: #ffffff;
  border-color: #7367f0;
  box-shadow: 0 8px 18px -8px rgba(115, 103, 240, 0.65);
}

.single-product-sort-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.single-product-sort-item {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #ebe9f1;
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: move;
  transition: all 0.2s ease;
}

.single-product-sort-item.dragging {
  opacity: 0.7;
  border-color: #7367f0;
  box-shadow: 0 12px 24px -16px rgba(115, 103, 240, 0.45);
}

.single-product-sort-item__handle {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
}

.single-product-sort-item__content {
  flex: 1 1 auto;
  min-width: 0;
}

.single-product-sort-item__badge {
  border: 1px solid #e5e7eb;
  background: #f8f8fb;
  color: #6b7280;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.theme-button-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e4e4ef;
}
.theme-field-card--compact {
  height: 100%;
  padding: 0.85rem;
}
.theme-field-card--compact label {
  min-height: 28px;
  line-height: 1.25;
}
.theme-field-card--compact .form-control-lg,
.theme-field-card--compact .custom-select.form-control-lg {
  min-height: 40px;
  font-size: 0.9rem;
}
.theme-field-card--compact small {
  font-size: 0.75rem;
}

</style>